import React from 'react';
import { motion } from 'framer-motion';
import { ChartBarIcon, BoltIcon, CurrencyDollarIcon } from '@heroicons/react/24/outline';

const MarketMaking: React.FC = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-primary-900 to-primary-800 text-white pb-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl pt-8">
            <motion.h2 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-5xl font-bold mb-6"
            >
              Market Making
            </motion.h2>
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-xl mb-8"
            >
              Participate in Eigensurance's prediction markets to improve risk assessment and earn rewards.
            </motion.p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-16">
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          <motion.div 
            whileHover={{ scale: 1.05 }}
            className="p-6 rounded-xl border border-secondary-200"
          >
            <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mb-4">
              <ChartBarIcon className="w-6 h-6 text-primary-600" />
            </div>
            <h3 className="text-xl font-semibold mb-2">Prediction Markets</h3>
            <ul className="list-disc list-inside text-secondary-600 mt-2 space-y-1">
              <li>Submit wildfire prediction models</li>
              <li>Monetize accurate predictions</li>
              <li>Improve system-wide risk assessment</li>
              <li>Earn rewards for successful models</li>
            </ul>
          </motion.div>

          <motion.div 
            whileHover={{ scale: 1.05 }}
            className="p-6 rounded-xl border border-secondary-200"
          >
            <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mb-4">
              <BoltIcon className="w-6 h-6 text-primary-600" />
            </div>
            <h3 className="text-xl font-semibold mb-2">Validator Rewards</h3>
            <ul className="list-disc list-inside text-secondary-600 mt-2 space-y-1">
              <li>Stake ETH through EigenLayer</li>
              <li>Earn fees from policy issuance</li>
              <li>Claims processing rewards</li>
              <li>Subject to slashing for incorrect validations</li>
            </ul>
          </motion.div>

          <motion.div 
            whileHover={{ scale: 1.05 }}
            className="p-6 rounded-xl border border-secondary-200"
          >
            <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mb-4">
              <CurrencyDollarIcon className="w-6 h-6 text-primary-600" />
            </div>
            <h3 className="text-xl font-semibold mb-2">Market Incentives</h3>
            <ul className="list-disc list-inside text-secondary-600 mt-2 space-y-1">
              <li>Dynamic reward scaling</li>
              <li>Performance-based bonuses</li>
              <li>Long-term staking benefits</li>
              <li>Community governance rewards</li>
            </ul>
          </motion.div>
        </div>

        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold mb-6">How to Participate</h2>
          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-semibold mb-2">1. Model Submission</h3>
              <p className="text-secondary-600">
                Develop and submit your wildfire prediction models. Models can incorporate various 
                data sources including satellite imagery, weather data, and historical patterns.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2">2. Validation Period</h3>
              <p className="text-secondary-600">
                Models undergo a validation period where their predictions are tested against 
                real-world data. Accuracy and reliability are key metrics for success.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2">3. Reward Distribution</h3>
              <p className="text-secondary-600">
                Earn rewards based on your model's performance. Successful predictions contribute 
                to the platform's risk assessment and earn proportional rewards.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2">4. Continuous Improvement</h3>
              <p className="text-secondary-600">
                Refine your models based on performance data and changing conditions. 
                The platform encourages ongoing optimization and adaptation.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarketMaking; 